"use client";

// Living Newsroom Pulse — ambient background layer behind hero text.
// Zwei Elemente: dünne Signal-Linien mit reisenden Pulsen + sporadisch aufploppende Quell-Indikatoren.

import { useEffect, useState } from "react";

const SOURCES = ["REU", "SRF", "ORF", "BBC", "APA", "SDA", "ARD", "LRT", "NZZ", "DPA"];

type Pop = { id: number; top: number; left: number; src: string; time: string };

function formatTime(d: Date) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Zufällige Position NUR in der rechten Hälfte — Text-Spalte links bleibt komplett frei
function randomPosition() {
  const zones = [
    { topMin: 8, topMax: 22, leftMin: 62, leftMax: 92 },   // oben rechts
    { topMin: 30, topMax: 45, leftMin: 72, leftMax: 94 },  // rechte Mitte oben
    { topMin: 55, topMax: 70, leftMin: 68, leftMax: 92 },  // rechte Mitte unten
    { topMin: 78, topMax: 90, leftMin: 62, leftMax: 90 },  // unten rechts
  ];
  const z = zones[Math.floor(Math.random() * zones.length)];
  return {
    top: z.topMin + Math.random() * (z.topMax - z.topMin),
    left: z.leftMin + Math.random() * (z.leftMax - z.leftMin),
  };
}

export function HeroAmbient() {
  const [pops, setPops] = useState<Pop[]>([]);

  useEffect(() => {
    let counter = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const schedule = () => {
      // alle 2.5–4.5s ein neuer Pop
      const wait = 2500 + Math.random() * 2000;
      timeoutId = setTimeout(() => {
        const id = counter++;
        const { top, left } = randomPosition();
        const src = SOURCES[Math.floor(Math.random() * SOURCES.length)];
        const time = formatTime(new Date());

        setPops((curr) => [...curr, { id, top, left, src, time }]);

        // nach 3s wieder entfernen
        setTimeout(() => {
          setPops((curr) => curr.filter((p) => p.id !== id));
        }, 3200);

        schedule();
      }, wait);
    };

    schedule();
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Signal-Linien — dünn, auf varianten Höhen, mit reisenden Pulsen */}
      <SignalLine top="12%" delay={0} duration={14} />
      <SignalLine top="28%" delay={6} duration={18} />
      <SignalLine top="48%" delay={3} duration={16} />
      <SignalLine top="68%" delay={9} duration={20} />
      <SignalLine top="84%" delay={2} duration={15} />
      <SignalLine top="94%" delay={11} duration={17} />

      {/* Source-Pops */}
      {pops.map((p) => (
        <SourcePop key={p.id} top={p.top} left={p.left} src={p.src} time={p.time} />
      ))}
    </div>
  );
}

function SignalLine({ top, delay, duration }: { top: string; delay: number; duration: number }) {
  // Linie lebt nur auf der rechten Hälfte — Text-Spalte bleibt frei
  return (
    <div
      className="absolute right-0 h-px signal-line"
      style={{ top, left: "55%" }}
    >
      {/* reisender Puls */}
      <span
        className="absolute top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[var(--color-blue)] signal-pulse"
        style={{
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
          boxShadow: "0 0 12px var(--color-blue)",
        }}
      />
      <style jsx>{`
        .signal-line {
          /* Linie fadet links aus, wird rechts sichtbar */
          background: linear-gradient(
            to right,
            transparent 0%,
            rgba(10, 10, 15, 0.02) 15%,
            rgba(10, 10, 15, 0.12) 50%,
            rgba(10, 10, 15, 0.12) 100%
          );
        }
        .signal-pulse {
          animation-name: travel;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          left: 0%;
          opacity: 0;
        }
        @keyframes travel {
          0% {
            left: -4%;
            opacity: 0;
          }
          12% {
            opacity: 1;
          }
          88% {
            opacity: 1;
          }
          100% {
            left: 104%;
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .signal-pulse {
            animation: none;
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

function SourcePop({ top, left, src, time }: { top: number; left: number; src: string; time: string }) {
  return (
    <div
      className="absolute source-pop"
      style={{ top: `${top}%`, left: `${left}%`, transform: "translate(-50%, -50%)" }}
    >
      <div className="flex items-center gap-2">
        <span
          className="w-1.5 h-1.5 rounded-full bg-[var(--color-blue)]"
          style={{ boxShadow: "0 0 0 4px rgba(30,58,255,0.1)" }}
        />
        <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-[var(--color-ink-muted)] whitespace-nowrap">
          {time} <span className="text-[var(--color-blue)]">{src}</span>
        </span>
      </div>
      <style jsx>{`
        .source-pop {
          animation: fadeInOut 3.2s ease-in-out forwards;
        }
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translate(-50%, -40%);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
          80% {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -60%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .source-pop {
            animation: none;
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

// Blinkender Caret — dezenter Micro-Moment nach „funktionieren."
export function HeroCaret() {
  return (
    <>
      <span
        className="hero-caret inline-block align-baseline w-[3px] ml-1.5 bg-[var(--color-blue)]"
        style={{ height: "0.85em" }}
      />
      <style jsx>{`
        .hero-caret {
          animation: blink 1.1s steps(1, end) infinite;
        }
        @keyframes blink {
          0%,
          50% {
            opacity: 1;
          }
          50.01%,
          100% {
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-caret {
            animation: none;
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
